/**
 * <LiveReportDetailsSkeleton> — la forme d'un rapport de séance pendant son chargement.
 *
 * La carte de `<LiveReportDetailsTemplate>`, et dedans ce que tout rapport porte en tête : la
 * date et le prénom centrés, Zoé, son message ; puis « Bilan de la session » et ses puces, et une
 * carte de section. La suite d'un rapport dépend de son contenu (scores, chapitres, activités) :
 * le squelette ne la devine pas, elle arrive sous la ligne de flottaison.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { Card } from "../../molecules/Card"

/** Zoé, dans l'en-tête du rapport (`<ReportHero>`). */
const MASCOT_SIZE = 167

/** La puce d'une `<BulletList>` : un « • » en `text-2xl`, qui donne sa hauteur à la ligne. */
const BULLET_LINE = 35
const BULLET_WIDTH = 10
const BULLET_DOT = 6

function BulletBones(props: { lines: number }) {
    return (
        <Row gap="2" align="start">
            <View style={styles.bullet}>
                <Skeleton shape="circle" size={BULLET_DOT} />
            </View>
            <View style={styles.fill}>
                {Array.from({ length: props.lines }, (_, index) => (
                    <Skeleton key={index} width={index === props.lines - 1 ? "60%" : "100%"} />
                ))}
            </View>
        </Row>
    )
}

export function LiveReportDetailsSkeleton() {
    return (
        <Card p="4">
            <Stack gap="5">
                <Stack gap="3" align="center">
                    <Skeleton textSize="text-2xl" width="60%" />
                    <Skeleton width="30%" />
                    <Skeleton shape="circle" size={MASCOT_SIZE} />
                    <View style={styles.centered}>
                        <Skeleton textSize="text-xl" width="85%" />
                        <Skeleton textSize="text-xl" width="55%" />
                    </View>
                </Stack>

                <Card p="5">
                    <Stack gap="4">
                        <View style={styles.centered}>
                            <Skeleton textSize="text-xl" width="55%" />
                        </View>
                        <Stack gap="2">
                            <BulletBones lines={2} />
                            <BulletBones lines={2} />
                            <BulletBones lines={1} />
                            <BulletBones lines={2} />
                        </Stack>
                    </Stack>
                </Card>

                <Card p="5">
                    <Stack gap="3">
                        <View style={styles.centered}>
                            <Skeleton textSize="text-xl" width="50%" />
                        </View>
                        <Skeleton width="90%" />
                        <Skeleton width="75%" />
                    </Stack>
                </Card>
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    fill: { flex: 1 },
    centered: { width: "100%", alignItems: "center" },
    bullet: { width: BULLET_WIDTH, height: BULLET_LINE, justifyContent: "center", alignItems: "center" },
}))
